import { z } from "zod";
import { trimRunEnd } from "../trimRuns";
import { dbtSourceWarningSchema } from "../dbt/sourceWarnings";

/** Instance reads and the destination binding (FR-21, SEC-2, SEC-11). */

/** The destination's "Table name case" (round 6): see `TableNameCase`. */
export const tableNameCaseSchema = z.enum(["native", "source", "lower"]);

/** The Destination page's labels for each table name case. */
export const TABLE_NAME_CASE_LABELS: Readonly<Record<z.infer<typeof tableNameCaseSchema>, string>> =
  {
    native: "Native",
    source: "Keep source case",
    lower: "Lower case (quoted)",
  };

/** The hint under the Destination page's "Table name case". */
export const TABLE_NAME_CASE_HINT =
  "Native stores names the way an unquoted name folds: upper case on Snowflake, lower case on Databricks. Keep source case (Snowflake only) keeps each source table's spelling. Lower case (Snowflake only) stores the whole name in lower case, for example sales_crm_orders. With either of those two, queries and dbt sources must put the table name in double quotes. Applies to tables created from now on. A table already loaded keeps its name.";

/**
 * #2488: under the field while Keep source case or Lower case is chosen and a dbt repository is
 * connected, and on Your data while the destination uses either (a deployment policy can fix the
 * case and hide the field; Your data does not read whether a repository is connected, so it says
 * "If"). Snowflake folds an unquoted name to upper case, so a dbt source that names the table bare
 * does not find it.
 */
export const TABLE_NAME_CASE_DBT_NOTE =
  "If a dbt project reads these tables, it must quote their names too: set quoting identifier to true on its sources, and spell each name as Your data shows it.";

/**
 * #2459: under the field while the chosen case differs from the saved one, so the person reads
 * what a change does to the tables already loaded before saving it (`entity_table_case` keeps
 * each table's first case; a destination move confirmed with a reset clears those records,
 * `server/services/loadBounds.ts`, and deleting the connection does too).
 */
/**
 * #2459: the note instead of `TABLE_NAME_CASE_CHANGE_NOTE` when the same save also moves the
 * destination (type, account, database or schema): if tables have landed, the save asks to read
 * everything again, and that reset creates every table again in the new case.
 */
export const TABLE_NAME_CASE_MOVE_NOTE =
  "This save also moves the destination. If tables have already landed, you are asked to read everything again, and every table is then created again in the new place in the new case.";

export const TABLE_NAME_CASE_CHANGE_NOTE =
  "Tables already loaded keep their name and case after this change, even if they are dropped in Snowflake and loaded again. Only a table loaded for the first time after you save uses the new case. Moving the destination with a reset creates every table again in the new case.";

/** #2466: the Destination page's label for `metadataColumns`. */
export const METADATA_COLUMNS_LABEL = "Add skyData columns (load time and run id) to every table";

/** #2466: the hint under it. Off by default, and turning it off leaves the columns where they are. */
export const METADATA_COLUMNS_HINT =
  "Off by default. When on, every Snowflake table skyData loads gets two more columns: _SKYDATA_SYNCED_AT, when the row was loaded, and _SKYDATA_RUN_ID, the import that loaded it. Turning it off later leaves the two columns on tables that already have them. skyData stops filling them, so new rows have no run id and may have no load time, and updated rows keep the values of the import that last filled them.";

/** Why `source` is not offered (and refused) on a Databricks destination. */
export const KEEP_SOURCE_CASE_UNAVAILABLE =
  "Keep source case is not available on Databricks: Unity Catalog stores every table name in lower case.";

/** #2459: why `lower` is not offered (and refused) on a Databricks destination. */
export const LOWER_CASE_UNAVAILABLE =
  "Lower case is not needed on Databricks: Unity Catalog stores every table name in lower case already, so choose Native.";

export const instanceViewSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  environmentLabel: z.string().nullable(),
  status: z.enum(["provisioning", "active", "disabled"]),
  tenantId: z.uuid(),
  /** The tenant's Name (`tenants.name`, platform 0025; the former slug). */
  tenantName: z.string(),
  /** The tenant's Display name (`tenants.display_name`), the name the app shows. */
  tenantDisplayName: z.string(),
  provisionedAt: z.string().nullable(),
});
export type InstanceView = z.infer<typeof instanceViewSchema>;

/**
 * A `secret_ref` column crosses the wire as its pointer only: the vault and the secret name,
 * never a value and never a version (SEC-2). Null means the binding does not use that secret.
 */
export const secretPointerSchema = z
  .object({
    vaultName: z.string(),
    secretName: z.string(),
  })
  .nullable();
export type SecretPointer = z.infer<typeof secretPointerSchema>;

/**
 * #923: the destination's state as the screens read it (`shared/warehouse/destinationReadiness.ts`).
 * `lastTest` is the last Test on the Destination screen (mode `test`); Check now and the daily
 * check are in `GET /instances/current/credentials`. `advice` is the last failing test's sentence,
 * next step and SQL, answered by the GET only (the role and user withheld below
 * `instance.binding.identity.read`).
 */
export const destinationReadinessViewSchema = z.object({
  state: z.enum(["not_set", "draft", "ready"]),
  reason: z
    .enum(["incomplete", "never_tested", "test_failed", "changed", "databricks_coming_soon"])
    .nullable(),
  /** The empty fields of an `incomplete` destination, by field id. */
  missing: z.array(z.string()),
  source: z.enum(["test", "carried"]).nullable(),
  lastTest: z
    .object({
      at: z.string(),
      /** The tester's display name; null when the user is gone. */
      by: z.string().nullable(),
      outcome: z.enum(["passed", "failed"]),
      code: z.string().nullable(),
    })
    .nullable(),
  advice: z
    .object({ sentence: z.string(), nextStep: z.string().nullable(), sql: z.array(z.string()) })
    .nullable()
    .optional(),
});
export type DestinationReadinessView = z.infer<typeof destinationReadinessViewSchema>;

/**
 * #1106: `GET /instances/current/destination-summary`, what the Connections list and editor need
 * from the destination for a reader of connections (`dataflows.read`), now that the binding GET is
 * Instance admin and above: its type, whether its address is set, and how table names are spelled.
 * The same facts the Overview and Your data already show every role. No account, database, schema,
 * stage, repository, identity or pointer. Not an audited read.
 */
export const destinationSummaryViewSchema = z.strictObject({
  /** Null: no binding row. */
  type: z.enum(["snowflake", "databricks"]).nullable(),
  /** The type's own address is set (`snowflake_account`, `databricks_host`). */
  bound: z.boolean(),
  tableNameCase: tableNameCaseSchema,
  /** #2501: the deployment policy fixes the Table name case (no screen offers to change it). */
  tableNameCaseFixed: z.boolean().optional(),
});
export type DestinationSummaryView = z.infer<typeof destinationSummaryViewSchema>;

export const instanceBindingViewSchema = z.object({
  instanceId: z.uuid(),
  destinationType: z.enum(["snowflake", "databricks"]),
  adlsAccount: z.string().nullable(),
  adlsContainerStaging: z.string().nullable(),
  adlsContainerLake: z.string().nullable(),
  snowflakeAccount: z.string().nullable(),
  snowflakeWarehouse: z.string().nullable(),
  snowflakeDatabase: z.string().nullable(),
  snowflakeSchema: z.string().nullable(),
  /**
   * The loader identity (ADR 0011): `snowflakeRole`, `snowflakeUser` and `databricksSpClientId`
   * are ABSENT, not null, for a caller without `instance.binding.identity.read` (TenantAdmin and
   * above), so null still means "not set"; `identityWithheld` then says why they are missing.
   */
  snowflakeRole: z.string().nullable().optional(),
  snowflakeUser: z.string().nullable().optional(),
  snowflakePrivateKey: secretPointerSchema,
  snowflakeKeyPassphrase: secretPointerSchema,
  snowflakeStorageIntegration: z.string().nullable(),
  /** The external stage the load step COPYs from; null means the derived default. */
  snowflakeStageName: z.string().nullable(),
  /**
   * Round 6 (owner 2026-10-01 23:10): how a loaded table's name is spelled (`TableNameCase` in
   * `shared/warehouse/targetTableName.ts`). Optional so a view built before the column reads as
   * `native`, which is what every binding had.
   */
  tableNameCase: tableNameCaseSchema.optional(),
  /**
   * #2466 (owner 2026-11-01): the load adds `_SKYDATA_SYNCED_AT` and `_SKYDATA_RUN_ID` to every
   * Snowflake table it loads. Optional so a view built before the column reads as off, the default.
   */
  metadataColumns: z.boolean().optional(),
  /**
   * #2501: the fields this deployment's policy fixes (`DEPLOYMENT_POLICY`). Their values above are
   * the policy's; the Destination page renders no control for them, and a save of another value
   * is a 400 on the field. Absent: none.
   */
  fixedByDeployment: z.array(z.enum(["tableNameCase", "metadataColumns"])).optional(),
  databricksHost: z.string().nullable(),
  databricksSpClientId: z.string().nullable().optional(),
  databricksSpSecret: secretPointerSchema,
  databricksWarehouseId: z.string().nullable(),
  dbtRepoUrl: z.string().nullable(),
  /** The git ref the transform clones (branch, tag or 40-hex sha); null means `main`. */
  dbtBranch: z.string().nullable(),
  /** Where `dbt_project.yml` lives inside the clone; null means the repository root. */
  dbtProjectSubdir: z.string().nullable(),
  dbtGitSecret: secretPointerSchema,
  teamsWebhook: secretPointerSchema,
  /**
   * Which secrets are stored, one boolean per pointer field above (SEC-17, round 3). The GET
   * answers the pointers themselves only to a caller holding `instance.settings.write`; any
   * other caller reads every pointer as null and this map instead, so the screen can still say
   * "Stored" without the vault and secret names. Optional: the write routes answer the writer,
   * who reads the pointers, and a view built before this field reads as before.
   */
  /** True when the loader identity was left out for this caller (ADR 0011, see above). */
  identityWithheld: z.boolean().optional(),
  /** #923: Draft or Ready. Optional: a view built before the rule reads as before. */
  readiness: destinationReadinessViewSchema.optional(),
  /**
   * #1674: on a dbt repository save's answer only, what the save is worth a second look for
   * (`REF_NOT_FOUND`). Never on a read.
   */
  warnings: z.array(dbtSourceWarningSchema).optional(),
  secretsStored: z
    .object({
      snowflakePrivateKey: z.boolean(),
      snowflakeKeyPassphrase: z.boolean(),
      databricksSpSecret: z.boolean(),
      dbtGitSecret: z.boolean(),
      teamsWebhook: z.boolean(),
    })
    .optional(),
});
export type InstanceBindingView = z.infer<typeof instanceBindingViewSchema>;
export type BindingSecretsStored = NonNullable<InstanceBindingView["secretsStored"]>;

/**
 * What the platform provisioned for one instance, for the platform admin's "Platform resources"
 * section of the destination page (`GET /instances/current/platform-resources`,
 * `platform.instances.manage`). Names only (SEC-17): never a key, a SAS, a connection string or
 * any secret value, so every field is safe to show and to copy. Null means not known here: a
 * development deployment has no Key Vault or job identity, and an instance with no binding row
 * has no storage recorded yet.
 */
export const instancePlatformResourcesSchema = z.object({
  /** Which secret store the deployment writes `secret_ref` values to. */
  secretStore: z.enum(["keyvault", "local"]),
  /** The Key Vault that store writes to (`KEY_VAULT_NAME`); null with the local store. */
  keyVaultName: z.string().nullable(),
  /**
   * The storage account the instance's files are staged in: the binding's `adls_account`, else
   * the deployment's `ADLS_ACCOUNT`, the rule the runner stages by (`buildRunnerContext`).
   */
  storageAccount: z.string().nullable(),
  /**
   * Where `storageAccount` came from (round 6 walk W13): `binding` (this instance's own),
   * `deployment` (the `ADLS_ACCOUNT` default a new instance's imports use), null when neither
   * names one. The screen marks the default, so it never reads "Not set" while imports work.
   */
  storageAccountSource: z.enum(["binding", "deployment"]).nullable(),
  stagingContainer: z.string().nullable(),
  /** The tenant database host, the database and this instance's schema in it (ADR 0005). */
  tenantDatabaseServer: z.string(),
  tenantDatabaseName: z.string(),
  instanceSchemaName: z.string(),
  /** The user-assigned identity import jobs run as (last segment of its resource id). */
  managedIdentityName: z.string().nullable(),
  /**
   * Which kind of identity the jobs run as: `ACA_JOB_IDENTITY_RESOURCE_ID=system` is the
   * system-assigned one, which has no name of its own. Null when neither is known.
   */
  managedIdentityKind: z.enum(["user-assigned", "system-assigned"]).nullable(),
});
export type InstancePlatformResources = z.infer<typeof instancePlatformResourcesSchema>;

/* ------------------------------------------------------------------------------------- *
 * The dbt git remote (SEC-2, session-4 fix-round answer 35)
 * ------------------------------------------------------------------------------------- */

/**
 * Longest `instance_bindings.dbt_repo_url` anything accepts. The column is `text`; the bound is
 * here so the write body, the runner's `assertDbtRepoUrl` and the refusal message agree.
 */
export const MAX_DBT_REPO_URL_LENGTH = 2000;

/**
 * #1250: the refusals of a repository address are customer copy. An instance admin reads them on
 * Settings, dbt repository, beside the field (the server's `VALIDATION_FAILED` issue, and the
 * form's own check through this same function), so each says what to do in plain words: no
 * internal word (secret_ref, binding, image, PAT), no semicolon, a full stop at the end. None of
 * them echoes the address, so none can carry the token it refuses (`scripts/check-copy.mjs`
 * reads this file).
 */
export const DBT_REPO_URL_CREDENTIAL_MESSAGE =
  "Remove the token from the address. Add it in Access token instead.";
export const DBT_REPO_URL_HTTPS_MESSAGE =
  "Use the repository's https address, the one that starts with https://.";
export const DBT_REPO_URL_UNREADABLE_MESSAGE =
  "This is not a repository address. Copy the https address from your git host.";

/** The address is too long or has a space or a character outside plain letters and symbols. */
function dbtRepoUrlCharsetMessage(length: number): string {
  const limit = MAX_DBT_REPO_URL_LENGTH.toLocaleString("en-US");
  const base = `Enter the address with no spaces or special characters, in at most ${limit} characters.`;
  return length > MAX_DBT_REPO_URL_LENGTH
    ? `${base} This one has ${length.toLocaleString("en-US")}.`
    : base;
}

/**
 * Why a dbt repository URL is not usable, or `null` when it is.
 *
 * One function so that the write boundary (`updateBindingBodySchema` in `./instancesAdmin`, and
 * `BindingService` behind it) and the bundle boundary (`assertDbtRepoUrl` in
 * `runner/executors/DbtJobExecutor.ts`) cannot disagree about which URLs exist: a value the
 * editor accepts and the bundle refuses is a 200 at the screen and a failed nightly run hours
 * later, which is the trade fix-round answer 35 was written to remove.
 *
 * Every returned message is safe to store, log and show: no branch echoes the value as given.
 * The charset branch is the one whitespace lands on — a pasted token with a stray space — so it
 * names the length and the rule and no part of the value at all, since a redaction regex cannot
 * be trusted to find the authority in a string that is not a URL.
 */
export function dbtRepoUrlRefusal(url: string): string | null {
  // Printable ASCII with no space: refuses whitespace, control characters and the invisible
  // look-alikes a hostname could otherwise carry, in one expression.
  if (url.length === 0 || url.length > MAX_DBT_REPO_URL_LENGTH || !/^[!-~]+$/.test(url)) {
    return dbtRepoUrlCharsetMessage(url.length);
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return DBT_REPO_URL_UNREADABLE_MESSAGE;
  }
  // Before the scheme check, so the shape that carries a credential is the one answered first.
  if (parsed.username || parsed.password) return DBT_REPO_URL_CREDENTIAL_MESSAGE;
  if (parsed.protocol !== "https:") return DBT_REPO_URL_HTTPS_MESSAGE;
  if (!parsed.hostname) return DBT_REPO_URL_UNREADABLE_MESSAGE;
  return null;
}

/* ------------------------------------------------------------------------------------- *
 * The dbt ref and project subdirectory (platform migration 0005, session 6)
 * ------------------------------------------------------------------------------------- */

/**
 * The bounds and charsets `runner/executors/DbtJobExecutor.ts` applies to `repo.ref` and
 * `repo.subdir` when it writes a bundle (`assertDbtRef`, `assertDbtSubdir`). They are restated
 * here for the binding WRITE boundary for the same reason `dbtRepoUrlRefusal` is: a value the
 * destination screen accepts and the bundle refuses is a 200 at the editor and a failed nightly
 * run hours later. `BindingService.dbtSubdir.test.ts` holds the two ends to one answer over a
 * table of values, so neither can drift without a test naming the value that moved.
 */
export const MAX_DBT_REF_LENGTH = 255;
export const MAX_DBT_SUBDIR_LENGTH = 255;
const DBT_REF_CHARSET = /^[A-Za-z0-9._/-]+$/;
const DBT_SHA = /^[0-9a-f]{40}$/;
const DBT_SUBDIR_CHARSET = /^[A-Za-z0-9._/-]+$/;

/**
 * Why a git ref is not one the image can check out, or `null` when it is.
 *
 * A 40-hex sha is always accepted. Otherwise: the charset, the length, and the three of git's
 * own refname rules that matter for a value that reaches `git fetch origin <ref>` — no leading
 * `-` (a flag), no `..` (git refuses it, and it is the shape a traversal takes), no `.lock`
 * suffix, trailing `/` or trailing `.` (refs git will not resolve). Each message names the rule
 * the value breaks (#1250) and does not echo the value.
 */
export function dbtRefRefusal(ref: string): string | null {
  if (DBT_SHA.test(ref)) return null;
  // #1250: plain sentences that say what to enter (no "ref", "sha" or "40-hex"), one per rule,
  // so a value made only of the allowed characters is told the rule it actually breaks.
  if (ref.length === 0) return "Enter a branch, a tag or a full commit id.";
  if (ref.length > MAX_DBT_REF_LENGTH) {
    return `Use at most ${MAX_DBT_REF_LENGTH} characters for the branch or tag. This one has ${ref.length.toLocaleString("en-US")}.`;
  }
  if (!DBT_REF_CHARSET.test(ref)) {
    return 'Use only letters, digits, ".", "_", "-" and "/" in the branch or tag.';
  }
  if (ref.startsWith("-") || ref.startsWith("/")) {
    return 'A branch or tag cannot start with "-" or "/".';
  }
  if (ref.includes("..")) return "A branch or tag cannot have two full stops in a row.";
  if (ref.endsWith(".lock") || ref.endsWith("/") || ref.endsWith(".")) {
    return 'A branch or tag cannot end with ".lock", "/" or a full stop.';
  }
  return null;
}

/**
 * The stored form of a project subdirectory: trimmed, trailing slashes dropped.
 *
 * `dbt/` and `dbt` are one directory, and a separator is not a meaning, so this is the one
 * rewrite the write boundary performs; everything else is refused (`dbtProjectSubdirRefusal`).
 * The runner normalises the same way before it writes the bundle, so the stored value and the
 * bundle's `repo.subdir` are identical.
 */
export function normaliseDbtProjectSubdir(value: string): string {
  return trimRunEnd(value.trim(), "/");
}

/**
 * Why a project subdirectory is not usable, or `null` when it is.
 *
 * Relative only: no leading `/`, no leading `-`, no `..`, `.` or empty segment anywhere, a
 * charset of letters, digits, `.`, `_`, `-` and `/`, and at most `MAX_DBT_SUBDIR_LENGTH`
 * characters after normalisation. The container joins the value onto the clone and runs dbt
 * with `--project-dir`, so a value that walks upwards would point dbt at the image's own files.
 * Checked segment by segment, so `a..b` — a legal directory name — is not refused.
 */
export function dbtProjectSubdirRefusal(value: string): string | null {
  const trimmed = normaliseDbtProjectSubdir(value);
  // #1250: plain sentences, one per rule, and no echo of the value.
  if (trimmed.length === 0) {
    return "Enter the folder inside the repository, such as dbt or analytics/dbt.";
  }
  if (trimmed.length > MAX_DBT_SUBDIR_LENGTH) {
    return `Use at most ${MAX_DBT_SUBDIR_LENGTH} characters for the folder. This one has ${trimmed.length.toLocaleString("en-US")}.`;
  }
  if (!DBT_SUBDIR_CHARSET.test(trimmed)) {
    return 'Use only letters, digits, ".", "_", "-" and "/" in the folder.';
  }
  if (trimmed.startsWith("/") || trimmed.startsWith("-")) {
    return 'The folder cannot start with "/" or "-".';
  }
  const segments = trimmed.split("/");
  if (segments.some((segment) => segment === "..")) {
    return 'The folder must be inside the repository. Remove the "..".';
  }
  if (segments.some((segment) => segment === "" || segment === ".")) {
    return 'Remove the "." or empty part between the slashes in the folder.';
  }
  return null;
}
